import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Globe,
  Award,
  ArrowRight,
  Leaf,
  HardHat,
  Lock,
  FlaskConical,
  Microscope,
  Cpu,
  Utensils,
  Boxes,
  Zap,
  ScanLine,
  Apple,
  Sprout,
  Factory,
  FileCheck,
  ClipboardCheck,
  HeartPulse,
  Gauge,
  Truck,
  BookOpen,
  Lightbulb,
  Landmark,
  BriefcaseBusiness,
  Handshake,
  Scale,
  Rocket,
  Flame,
  Wifi,
  Database,
  Settings,
  Building2,
  Users,
  BarChart3,
  Bug,
  Droplets,
  Recycle,
  Activity,
  CircuitBoard,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const auditorTrainings = [
  // MANAGEMENT SYSTEMS
  {
    iso: "ISO 9001",
    title: "Quality Management System",
    category: "Management Systems",
    icon: ClipboardCheck,
    link: "/iso-lead-auditor-training/iso-9001",
  },
  {
    iso: "ISO 13485",
    title:
      "Quality Management Systems specific to the medical devices industry",
    category: "Management Systems",
    icon: Leaf,
    link: "/iso-lead-auditor-training/iso-13485",
  },
  {
    iso: "ISO 14001",
    title: "Environmental Management System",
    category: "Management Systems",
    icon: Leaf,
    link: "/iso-lead-auditor-training/iso-14001",
  },
  {
    iso: "ISO 45001",
    title: "Occupational Health & Safety Management",
    category: "Management Systems",
    icon: HardHat,
    link: "/iso-lead-auditor-training/iso-45001",
  },
  {
    iso: "ISO 50001",
    title: "Energy Management System",
    category: "Management Systems",
    icon: Zap,
    link: "/iso-lead-auditor-training/iso-50001",
  },
  {
    iso: "ISO 22301",
    title: "Business Continuity Management System",
    category: "Management Systems",
    icon: ShieldCheck,
    link: "/iso-lead-auditor-training/iso-22301",
  },
  {
    iso: "ISO 37001",
    title: "Anti-Bribery Management System",
    category: "Management Systems",
    icon: Scale,
  },
  {
    iso: "ISO 41001",
    title: "Facility Management System",
    category: "Management Systems",
    icon: Building2,
    link: "/iso-lead-auditor-training/iso-41001",
  },
  {
    iso: "ISO 55000",
    title: "Asset Management",
    category: "Management Systems",
    icon: Settings,
    link: "/iso-lead-auditor-training/iso-55000",
  },
  {
    iso: "ISO 31000",
    title: "Risk Management",
    category: "Management Systems",
    icon: Activity,
    link: "/iso-lead-auditor-training/iso-31000",
  },
  {
    iso: "ISO 20000-1",
    title: "IT Service Management",
    category: "Management Systems",
    icon: Cpu,
    link: "/iso-lead-auditor-training/iso-20000",
  },

  // IT & INFORMATION SECURITY
  {
    iso: "ISO 27001",
    title: "Information Security Management",
    category: "IT & Security",
    icon: Lock,
    link: "/iso-lead-auditor-training/iso-27001",
  },
  {
    iso: "ISO/IEC 27001:2022",
    title: "Information Security (2022 Version)",
    category: "IT & Security",
    icon: Lock,
    link: "/iso-lead-auditor-training/iso-27001-2022",
  },
  {
    iso: "ISO/IEC 27005",
    title: "Information Security Risk Management",
    category: "IT & Security",
    icon: Bug,
    link: "/iso-lead-auditor-training/iso-27005",
  },
  {
    iso: "ISO/IEC 27701",
    title: "Privacy Information Management",
    category: "IT & Security",
    icon: Database,
    link: "/iso-lead-auditor-training/iso-27701",
  },
  {
    iso: "ISO/IEC 27017",
    title: "Cloud Security Controls",
    category: "IT & Security",
    icon: Wifi,
    link: "/iso-lead-auditor-training/iso-27017",
  },
  {
    iso: "ISO/IEC 27018",
    title: "PII Protection in Cloud",
    category: "IT & Security",
    icon: Lock,
    link: "/iso-lead-auditor-training/iso-27018",
  },
  {
    iso: "ISO/IEC 27031",
    title: "ICT Readiness for Business Continuity",
    category: "IT & Security",
    icon: ShieldCheck,
    link: "/iso-lead-auditor-training/iso-27031",
  },
  {
    iso: "IT Risk Assessment & Treatment",
    title: "IT Risk Assessment & Treatment",
    category: "IT & Security",
    icon: BarChart3,
    link: "/iso-lead-auditor-training/it-risk-assessment",
  },
  {
    iso: "Third-Party & Supplier Security",
    title: "Third-Party & Supplier Security",
    category: "IT & Security",
    icon: Handshake,
    link: "/iso-lead-auditor-training/third-party-supplier-security",
  },

  // LABORATORY & INSPECTION
  {
    iso: "ISO 17025",
    title: "Testing & Calibration Laboratories",
    category: "Laboratory & Inspection",
    icon: Microscope,
    link: "/iso-lead-auditor-training/iso-17025",
  },
  {
    iso: "ISO 17020",
    title: "Inspection Bodies",
    category: "Laboratory & Inspection",
    icon: FileCheck,
    link: "/iso-lead-auditor-training/iso-17020",
  },
  {
    iso: "ISO 17021",
    title: "Certification Bodies",
    category: "Laboratory & Inspection",
    icon: ClipboardCheck,
    link: "/iso-lead-auditor-training/iso-17021",
  },

  // FOOD SAFETY
  {
    iso: "ISO 22000",
    title: "Food Safety Management System",
    category: "Food Safety",
    icon: Utensils,
    link: "/iso-lead-auditor-training/iso-22000",
  },
  {
    iso: "ISO 17025 (TSCL)",
    title: "(Testing & Calibration Labs)",
    category: "Food Safety",
    icon: Utensils,
    link: "/iso-lead-auditor-training/iso-17025TSCL",
  },
  {
    iso: "ISO 17065 (PCB)",
    title: "Product Certification Bodies",
    category: "Food Safety",
    icon: Apple,
    link: "/iso-lead-auditor-training/iso-17065",
  },
  {
    iso: "HACCP",
    title: "Hazard Analysis & Critical Control Points",
    category: "Food Safety",
    icon: ScanLine,
    link: "/iso-lead-auditor-training/haccp",
  },
  {
    iso: "BRCGS",
    title: "Brand Reputation Compliance Global Standards",
    category: "Food Safety",
    icon: FileCheck,
    link: "/iso-lead-auditor-training/brcgs",
  },
  {
    iso: "SQF",
    title: "Safe Quality Food",
    category: "Food Safety",
    icon: FlaskConical,
    link: "/iso-lead-auditor-training/sqf",
  },
  {
    iso: "IFS",
    title: "International Featured Standards",
    category: "Food Safety",
    icon: Gauge,
    link: "/iso-lead-auditor-training/ifs",
  },
  {
    iso: "GMP",
    title: "Good Manufacturing Practices",
    category: "Food Safety",
    icon: Factory,
    link: "/iso-lead-auditor-training/gmp",
  },
  {
    iso: "GLOBALG.A.P",
    title: "Good Agricultural Practices",
    category: "Food Safety",
    icon: Sprout,
    link: "/iso-lead-auditor-training/globalgap",
  },
  {
    iso: "USFDA",
    title: "US Food & Drug Administration Compliance",
    category: "Food Safety",
    icon: HeartPulse,
    link: "/iso-lead-auditor-training/usfda",
  },
  {
    iso: "SFDA",
    title: "Saudi Food & Drug Authority Compliance",
    category: "Food Safety",
    icon: HeartPulse,
    link: "/iso-lead-auditor-training/sfda",
  },
  {
    iso: "Canadian Food Inspection Agency",
    title: "CFIA Compliance",
    category: "Food Safety",
    icon: ClipboardCheck,
    link: "/iso-lead-auditor-training/cfia",
  },

  // SUSTAINABILITY & SOCIAL
  {
    iso: "EcoVadis Support",
    title: "EcoVadis Rating Support",
    category: "Sustainability",
    icon: Award,
    link: "/iso-lead-auditor-training/ecovadis-support",
  },
  {
    iso: "Carbon / GHG",
    title: "Carbon Footprint & GHG Accounting",
    category: "Sustainability",
    icon: Flame,
    link: "/iso-lead-auditor-training/carbon-ghg",
  },
  {
    iso: "ISCC",
    title: "International Sustainability & Carbon Certification",
    category: "Sustainability",
    icon: Leaf,
    link: "/iso-lead-auditor-training/iscc",
  },
  {
    iso: "GSTC",
    title: "Global Sustainable Tourism Council",
    category: "Sustainability",
    icon: Globe,
    link: "/iso-lead-auditor-training/gstc",
  },
  {
    iso: "CDP",
    title: "Carbon Disclosure Project",
    category: "Sustainability",
    icon: BarChart3,
    link: "/iso-lead-auditor-training/cdp",
  },
  {
    iso: "AMFORI BSCI",
    title: "Business Social Compliance Initiative",
    category: "Sustainability",
    icon: Users,
    link: "/iso-lead-auditor-training/amfori-bsci",
  },
  {
    iso: "SEDEX",
    title: "Supplier Ethical Data Exchange",
    category: "Sustainability",
    icon: Handshake,
    link: "/iso-lead-auditor-training/sedex-semeta",
  },
  {
    iso: "HIGG Index",
    title: "Sustainability Assessment Tool",
    category: "Sustainability",
    icon: Gauge,
    link: "/iso-lead-auditor-training/higg-index",
  },
  {
    iso: "WRAP",
    title: "Worldwide Responsible Accredited Production",
    category: "Sustainability",
    icon: Award,
    link: "/iso-lead-auditor-training/wrap",
  },
  {
    iso: "Green Building Certification",
    title: "Green Building Certification",
    category: "Sustainability",
    icon: Landmark,
    link: "/iso-lead-auditor-training/green-building",
  },

  // AUDIT & CONSULTING
  {
    iso: "Internal Audits",
    title: "Internal Audit Services",
    category: "Consulting & Audit",
    link: "/iso-lead-auditor-training/internal-audits",
    icon: FileCheck,
  },
  {
    iso: "Gap Analysis",
    title: "Gap Analysis Services",
    category: "Consulting & Audit",
    link: "/iso-lead-auditor-training/gap-analysis",
    icon: Bug,
  },
  {
    iso: "Documentation Development",
    title: "ISO Documentation Development",
    link: "/iso-lead-auditor-training/documentation-development",
    category: "Consulting & Audit",
    icon: BookOpen,
  },

  // SOC REPORTS
  {
    iso: "SOC Type 1",
    title: "Service Organization Control Type 1",
    category: "IT & Security",
    icon: FileCheck,
    link: "/iso-lead-auditor-training/soc-type-1",
  },
  {
    iso: "SOC Type 2",
    title: "Service Organization Control Type 2",
    category: "IT & Security",
    icon: FileCheck,
    link: "/iso-lead-auditor-training/soc-type-2",
  },
  {
    iso: "SOC Type 3",
    title: "Service Organization Control Type 3",
    category: "IT & Security",
    icon: FileCheck,
    link: "/iso-lead-auditor-training/soc-type-3",
  },
];

const categories = [
  "Management Systems",
  "IT & Security",
  "Laboratory & Inspection",
  "Food Safety",
  "Sustainability",
  "Consulting & Audit",
];

const categoryColors = {
  "Management Systems": "from-blue-600 to-indigo-700",
  "Food Safety": "from-green-600 to-emerald-700",
  Sustainability: "from-teal-600 to-cyan-700",
  "IT & Security": "from-violet-600 to-purple-700",
  "Industry Specific": "from-orange-500 to-red-600",
  "Business Excellence": "from-amber-500 to-orange-600",
};

const categoryBadgeColors = {
  "Management Systems": "bg-blue-50 text-blue-700 border-blue-200",
  "Food Safety": "bg-green-50 text-green-700 border-green-200",
  Sustainability: "bg-teal-50 text-teal-700 border-teal-200",
  "IT & Security": "bg-violet-50 text-violet-700 border-violet-200",
  "Industry Specific": "bg-orange-50 text-orange-700 border-orange-200",
  "Business Excellence": "bg-amber-50 text-amber-700 border-amber-200",
};

const getCategoryId = (category) =>
  category
    .toLowerCase()
    .replace(/&/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const ISO_Lead_Auditor_Trainings = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.slice(1);
    const targetSection = document.getElementById(targetId);

    if (targetSection) {
      targetSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>Internationally Recognized Programs</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            ISO Lead Auditor Trainings
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto">
            Advance your auditing career with {auditorTrainings.length}+
            internationally recognized certification training programs designed
            for professionals and consultants.
          </p>
        </motion.div>
      </section>

      {/* Training Cards by Category */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {categories.map((category, catIdx) => {
            const items = auditorTrainings.filter(
              (t) => t.category === category,
            );
            if (items.length === 0) return null;
            return (
              <motion.div
                key={category}
                id={getCategoryId(category)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: catIdx * 0.05 }}
                className="mb-12 scroll-mt-32"
              >
                {/* Category Header */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className={`w-1.5 h-8 rounded-full bg-gradient-to-b ${categoryColors[category]}`}
                  />
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                    {category}
                  </h2>
                  <span
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border ${categoryBadgeColors[category]}`}
                  >
                    {items.length} Programs
                  </span>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {items.map((item, index) => {
                    const Icon = item.icon;
                    const inner = (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.03 }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        className={`group relative bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-lg hover:border-[var(--bg-primary)] transition-all duration-300 h-full flex flex-col items-center text-center ${item.link ? "cursor-pointer" : ""}`}
                      >
                        {/* Icon */}
                        {Icon && (
                          <div className="w-10 h-10 rounded-lg bg-gray-50 group-hover:bg-[var(--bg-primary)] flex items-center justify-center mb-3 transition-colors duration-300">
                            <Icon
                              size={20}
                              className="text-[var(--bg-primary)] group-hover:text-white transition-colors duration-300"
                            />
                          </div>
                        )}
                        {/* ISO Code */}
                        <h3 className="text-sm font-bold text-gray-900 group-hover:text-[var(--bg-primary)] transition-colors leading-tight">
                          {item.iso}
                        </h3>
                        {/* Title */}
                        <p className="text-[11px] text-gray-500 mt-1 leading-tight line-clamp-2">
                          {item.title}
                        </p>
                        {/* Arrow indicator for linked cards */}
                        {item.link && (
                          <div className="mt-auto pt-2">
                            <ArrowRight
                              size={14}
                              className="text-gray-300 group-hover:text-[var(--bg-primary)] transition-colors"
                            />
                          </div>
                        )}
                      </motion.div>
                    );
                    return item.link ? (
                      <Link to={item.link} key={index} className="block">
                        {inner}
                      </Link>
                    ) : (
                      <div key={index}>{inner}</div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] rounded-3xl shadow-2xl p-10 text-center text-white"
          >
            <div className="flex justify-center mb-5">
              <Award size={36} className="text-orange-400" />
            </div>
            <h3 className="text-2xl font-bold mb-3">
              Become a Certified ISO Lead Auditor
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto text-sm">
              Gain global recognition, auditing competence, and professional
              credibility through structured ISO Lead Auditor certification
              programs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
            >
              <Globe size={18} /> Enroll Now
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO_Lead_Auditor_Trainings;
